import { ClampToEdgeWrapping, DataTexture, FloatType, RedFormat } from 'three'

const buffers = new WeakMap<
  ArrayBufferLike,
  Map<string, SharedHeatmapTexture>
>()

export interface SharedHeatmapTexture {
  texture: DataTexture
  retain: () => () => void
}

export function sharedHeatmapTexture(
  data: Readonly<Float32Array>,
  width: number,
  height: number
): SharedHeatmapTexture {
  let entries = buffers.get(data.buffer)
  if (!entries) {
    entries = new Map()
    buffers.set(data.buffer, entries)
  }
  const key = `${data.byteOffset}:${data.length}:${width}:${height}`
  const existing = entries.get(key)
  if (existing) return existing
  if (data.length !== width * height)
    throw new Error('Heatmap texture dimensions do not match its data')
  const texture = new DataTexture(
    new Float32Array(data),
    width,
    height,
    RedFormat,
    FloatType
  )
  texture.wrapS = texture.wrapT = ClampToEdgeWrapping
  texture.internalFormat = 'R32F'
  texture.generateMipmaps = false
  texture.needsUpdate = true
  let references = 0
  const entry: SharedHeatmapTexture = {
    texture,
    retain() {
      references++
      return () => {
        references--
        // StrictMode re-runs effects before this microtask. Keep live shared textures.
        queueMicrotask(() => {
          if (references === 0 && entries.get(key) === entry) {
            texture.dispose()
            entries.delete(key)
          }
        })
      }
    },
  }
  entries.set(key, entry)
  return entry
}
